/**
 * RadioGroup: choose one of a few options (cockatrice-ui `src/ui/RadioGroup.jsx`, ported to hyperscript; PLAN.md §0).
 * Native radios inside a fieldset, so arrow keys, Tab and screen readers behave as people expect.
 * variant "list" (default) or "cards" for big choices: Passkey vs Authenticator app, export formats.
 * options: [{ value, label, description, icon, badge, disabled }]. onChange(value, event).
 */
import './css/radio-group.css';
import { createSignal, createUniqueId, mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, each, el, spread, when, whenSome } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

/** One choice. */
export interface RadioOption {
  /** Value reported by onChange. */
  readonly value: string;
  /** Label. */
  readonly label: Child;
  /** Second line explaining the choice. */
  readonly description?: Child;
  /** Icon, shown in cards. */
  readonly icon?: IconName | undefined;
  /** Small pill after the label, e.g. "Recommended". */
  readonly badge?: string | undefined;
  /** Not selectable. */
  readonly disabled?: boolean | undefined;
  /** Ids of text elsewhere that also describes this option (a note shared by some of the options), after its description. */
  readonly describedBy?: string | undefined;
  /** This radio takes the first focus when its dialog opens (the native `autofocus`). */
  readonly autofocus?: boolean | undefined;
}

export interface RadioGroupProps extends Attributes {
  /** The question, shown as the legend. */
  readonly label?: Child;
  /** Keep the legend for screen readers only. */
  readonly hideLabel?: boolean | undefined;
  /** Help under the legend. */
  readonly help?: Child;
  /** Error under the options. */
  readonly error?: Child;
  /** The choices. */
  readonly options: readonly RadioOption[];
  /** Controlled value. */
  readonly value?: string | undefined;
  /** Uncontrolled starting value. */
  readonly defaultValue?: string | undefined;
  /** Called when a choice is made. */
  readonly onChange?: ((value: string, event: Event) => void) | undefined;
  /** list (default) or cards for big choices. */
  readonly variant?: 'list' | 'cards' | undefined;
  /** vertical (default) or horizontal. */
  readonly orientation?: 'vertical' | 'horizontal' | undefined;
  /** Radio name; generated when omitted. */
  readonly name?: string | undefined;
  /** One must be chosen. */
  readonly required?: boolean | undefined;
  /** Disables the whole group. */
  readonly disabled?: boolean | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

export default function RadioGroup(props: RadioGroupProps): Child {
  const p = mergeProps({ variant: 'list', orientation: 'vertical' } as const, props);
  const [local, rest] = splitProps(p, [
    'label',
    'hideLabel',
    'help',
    'error',
    'name',
    'options',
    'value',
    'defaultValue',
    'onChange',
    'variant',
    'orientation',
    'required',
    'disabled',
    'class',
  ]);
  const uid = createUniqueId();
  const name = (): string => local.name ?? `radio-${uid}`;
  const helpId = `${uid}-help`;
  const errId = `${uid}-err`;
  const [inner, setInner] = createSignal(local.defaultValue);
  const current = (): string | undefined => local.value ?? inner();

  const cls = (): string =>
    [
      'ui-radio-group',
      `ui-radio-group--${local.variant}`,
      `ui-radio-group--${local.orientation}`,
      Boolean(local.error) && 'is-invalid',
      local.disabled === true && 'is-disabled',
      local.class,
    ]
      .filter(Boolean)
      .join(' ');

  return el(
    'fieldset',
    spread(rest, {
      class: cls,
      disabled: () => local.disabled,
      'aria-describedby': () => [Boolean(local.error) && errId, Boolean(local.help) && helpId].filter(Boolean).join(' ') || undefined,
      'aria-invalid': () => (local.error ? 'true' : undefined),
      'aria-required': () => (local.required === true ? 'true' : undefined),
    }),
    when(
      () => Boolean(local.label),
      () => el('legend', { class: () => (local.hideLabel === true ? 'ui-radio-group__legend sr-only' : 'ui-radio-group__legend') }, () => local.label),
    ),
    when(
      () => Boolean(local.help),
      () => el('p', { class: 'ui-radio-group__help', id: helpId }, () => local.help),
    ),
    el(
      'div',
      { class: 'ui-radio-group__options' },
      each(
        () => local.options,
        (o) => {
          const id = `${uid}-${o.value}`;
          const on = (): boolean => current() === o.value;
          return el(
            'label',
            { class: () => ['ui-radio', on() && 'is-checked', o.disabled === true && 'is-disabled'].filter(Boolean).join(' '), for: id },
            el('input', {
              id,
              type: 'radio',
              class: 'ui-radio__input',
              name,
              value: () => o.value,
              checked: on,
              disabled: () => o.disabled,
              required: () => local.required,
              'aria-describedby': () => [Boolean(o.description) && `${id}-d`, o.describedBy].filter(Boolean).join(' ') || undefined,
              autofocus: o.autofocus === true,
              onChange: (event: Event) => {
                setInner(o.value);
                local.onChange?.(o.value, event);
              },
            }),
            whenSome(
              () => (local.variant === 'cards' ? o.icon : undefined),
              (icon) => el('span', { class: 'ui-radio__icon' }, component(Icon, { name: icon() })),
            ),
            el(
              'span',
              { class: 'ui-radio__text' },
              el(
                'span',
                { class: 'ui-radio__label' },
                () => o.label,
                when(
                  () => Boolean(o.badge),
                  () => el('span', { class: 'ui-radio__badge' }, () => o.badge),
                ),
              ),
              when(
                () => Boolean(o.description),
                () => el('span', { class: 'ui-radio__description', id: `${id}-d` }, () => o.description),
              ),
            ),
          );
        },
      ),
    ),
    when(
      () => Boolean(local.error),
      () => el('p', { class: 'ui-radio-group__error', id: errId }, component(Icon, { name: 'warn', size: 'sm' }), el('span', {}, () => local.error)),
    ),
  );
}
